import { c as _c } from "react/compiler-runtime";
import type { Icon } from '@onefootprint/icons';
import type { DataIdentifier } from '@onefootprint/request-types/dashboard';
import { LinkButton, SegmentedControl } from '@onefootprint/ui';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { hasEntityBankAccounts, hasEntityCards } from '@/entities/utils/has-entity';
import { useDecryptForm } from '@/entity/components/decrypt-form';
import type { WithEntityProps } from '@/entity/components/with-entity';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import useField from '../../../../hooks/use-field';
import BankAccountFields from './components/bank-account-fields';
import CardFields from './components/card-fields';
export type FieldsetProps = WithEntityProps & {
  iconComponent: Icon;
  title: string;
};
const Fieldset = t0 => {
  const $ = _c(53);
  const {
    entity,
    title,
    iconComponent: IconComponent
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "fieldset"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const decryptControls = useDecryptControls();
  const decryptForm = useDecryptForm();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== decryptForm.control) {
    t3 = {
      control: decryptForm.control,
      name: "fields",
      defaultValue: t2
    };
    $[2] = decryptForm.control;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const selectedFields = useWatch(t3);
  let t4;
  if ($[4] !== entity) {
    t4 = hasEntityCards(entity) && hasEntityBankAccounts(entity);
    $[4] = entity;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const hasCardsAndBankAccounts = t4;
  let t5;
  if ($[6] !== entity) {
    t5 = hasEntityCards(entity);
    $[6] = entity;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const [isCard, setIsCard] = useState(t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const [selectedItemDis, setSelectedItemDis] = useState(t6);
  const getFieldProps = useField(entity);
  let selectableFields;
  let t7;
  if ($[9] !== getFieldProps || $[10] !== selectedFields || $[11] !== selectedItemDis) {
    let t8;
    if ($[14] !== getFieldProps) {
      t8 = di => getFieldProps(di).canSelect;
      $[14] = getFieldProps;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    selectableFields = selectedItemDis.filter(t8);
    let t9;
    if ($[16] !== selectedFields) {
      t9 = field => selectedFields.includes(field);
      $[16] = selectedFields;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    t7 = selectableFields.every(t9);
    $[9] = getFieldProps;
    $[10] = selectedFields;
    $[11] = selectedItemDis;
    $[12] = selectableFields;
    $[13] = t7;
  } else {
    selectableFields = $[12];
    t7 = $[13];
  }
  const allSelected = t7;
  const hasSelectableFields = selectableFields.length > 0;
  const shouldShowSelectAll = decryptControls.step === "selecting-fields" && hasSelectableFields;
  let t8;
  if ($[18] !== decryptForm || $[19] !== selectableFields) {
    t8 = () => {
      decryptForm.setFields(selectableFields);
    };
    $[18] = decryptForm;
    $[19] = selectableFields;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleSelectAll = t8;
  let t9;
  if ($[21] !== decryptForm || $[22] !== selectableFields) {
    t9 = () => {
      decryptForm.unsetFields(selectableFields);
    };
    $[21] = decryptForm;
    $[22] = selectableFields;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleDeselectAll = t9;
  let t10;
  if ($[24] !== IconComponent) {
    t10 = <IconComponent />;
    $[24] = IconComponent;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t) {
    t11 = t(_temp);
    $[26] = t;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t10 || $[29] !== t11) {
    t12 = <h2 className="flex items-center gap-2 text-label-2 text-primary">{t10}{t11}</h2>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== decryptControls.step || $[32] !== hasCardsAndBankAccounts || $[33] !== isCard || $[34] !== t) {
    t13 = hasCardsAndBankAccounts && decryptControls.step !== "selecting-fields" && <SegmentedControl size="compact" variant="secondary" aria-label={t(_temp2)} options={[{
      label: t(_temp3),
      value: "cards"
    }, {
      label: t(_temp4),
      value: "bank-accounts"
    }]} value={isCard ? "cards" : "bank-accounts"} onChange={value => {
      setIsCard(value === "cards");
    }} />;
    $[31] = decryptControls.step;
    $[32] = hasCardsAndBankAccounts;
    $[33] = isCard;
    $[34] = t;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== allSelected || $[37] !== handleDeselectAll || $[38] !== handleSelectAll || $[39] !== shouldShowSelectAll || $[40] !== t) {
    t14 = shouldShowSelectAll && <LinkButton onClick={allSelected ? handleDeselectAll : handleSelectAll}>{allSelected ? t(_temp5) : t(_temp6)}</LinkButton>;
    $[36] = allSelected;
    $[37] = handleDeselectAll;
    $[38] = handleSelectAll;
    $[39] = shouldShowSelectAll;
    $[40] = t;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t12 || $[43] !== t13 || $[44] !== t14) {
    t15 = <header className="flex justify-between px-4 py-2 border-b border-solid border-tertiary rounded-t-[sm] bg-secondary h-[40px]">{t12}{t13}{t14}</header>;
    $[42] = t12;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== entity || $[47] !== isCard) {
    t16 = <div className="flex flex-col flex-1 gap-6 p-4">{isCard ? <CardFields entity={entity} setSelectedItemDis={setSelectedItemDis} /> : <BankAccountFields entity={entity} setSelectedItemDis={setSelectedItemDis} />}</div>;
    $[46] = entity;
    $[47] = isCard;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t15 || $[50] !== t16 || $[51] !== title) {
    t17 = <div aria-label={title} className="flex flex-col justify-between w-full h-full border border-solid rounded-sm border-tertiary">{t15}{t16}</div>;
    $[49] = t15;
    $[50] = t16;
    $[51] = title;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  return t17;
};
export default Fieldset;
function _temp($) {
  return $.financial.title;
}
function _temp2($_0) {
  return $_0.financial.control.aria;
}
function _temp3($_1) {
  return $_1.financial.control.cards;
}
function _temp4($_2) {
  return $_2.financial.control.bankAccounts;
}
function _temp5($_3) {
  return $_3.deselectAll;
}
function _temp6($_4) {
  return $_4.selectAll;
}